import { collection, collectionGroup, doc, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import type { PayrollPayment, PayrollReport, PayrollState } from '@gigdriver/domain';
import { callable } from './functions';
import { getFirestoreInstance } from './firestore';
import { fromFirestore } from './serde';

// Payroll reports are written ONLY by Cloud Functions (firestore.rules deny
// client writes) and readable by manager+; every mutation here is a callable.
// A gig has one live report, always at gigs/{gigId}/payrollReports/current.

interface GigRef {
  orgId: string;
  gigId: string;
}

export const generatePayrollReport = callable<GigRef, { reportId: string; version: number; totalCost: number; warnings: string[] }>(
  'generatePayroll',
);

export const finalizePayrollReport = callable<GigRef, { reportId: string; state: PayrollState; pdfPath: string }>('finalizePayroll');

export function markPayrollPaid(input: GigRef & { paidAt?: Date; reference?: string }) {
  return callable<GigRef & { paidAt?: string; reference?: string }, { reportId: string; state: PayrollState }>('markPayrollPaid')({
    ...input,
    paidAt: input.paidAt?.toISOString(),
  });
}

export const reopenPayrollReport = callable<GigRef & { reason: string }, { reportId: string; state: PayrollState }>('reopenPayroll');

// A fresh short-lived signed URL for the finalized PDF (a data: URL in the
// local emulator).
export const getPayrollPdfUrl = callable<GigRef, { url: string }>('getPayrollPdf');

function parse(data: unknown): PayrollReport {
  return fromFirestore<PayrollReport>(data);
}

// The gig's report (or null before one is generated), live.
export function watchGigPayrollReport(
  orgId: string,
  gigId: string,
  onData: (report: PayrollReport | null) => void,
  onError?: (err: unknown) => void,
): () => void {
  const ref = doc(getFirestoreInstance(), 'organizations', orgId, 'gigs', gigId, 'payrollReports', 'current');
  return onSnapshot(ref, snap => onData(snap.exists() ? parse(snap.data()) : null), err => onError?.(err));
}

// Every gig's report across the org, newest first (manager+; Payroll page).
export function watchOrgPayrollReports(
  orgId: string,
  onData: (reports: PayrollReport[]) => void,
  onError?: (err: unknown) => void,
): () => void {
  const q = query(
    collectionGroup(getFirestoreInstance(), 'payrollReports'),
    where('orgId', '==', orgId),
    orderBy('generatedAt', 'desc'),
  );
  return onSnapshot(q, snap => onData(snap.docs.map(d => parse(d.data()))), err => onError?.(err));
}

// ---- per-person payments (Planning/PersonPayments.md) ------------------------

export interface RecordPersonPaymentInput {
  orgId: string;
  personId: string;
  gigIds: string[];
  paidAt?: Date;
  reference?: string;
}

// Records a payment to one person covering their unpaid lines on the chosen
// finalized gigs. `pdfReady` is false only if the statement couldn't be stored
// immediately — getPaymentStatementUrl rebuilds it on demand either way.
export function recordPersonPayment(input: RecordPersonPaymentInput) {
  return callable<
    Omit<RecordPersonPaymentInput, 'paidAt'> & { paidAt?: string },
    { paymentId: string; total: number; name: string; pdfReady: boolean }
  >('recordPersonPayment')({ ...input, paidAt: input.paidAt?.toISOString() });
}

// A fresh short-lived signed URL for a payment's statement PDF (a data: URL in
// the local emulator).
export const getPaymentStatementUrl = callable<{ orgId: string; paymentId: string }, { url: string }>('getPaymentStatementPdf');

// Every recorded payment for the org, newest first (manager+).
export function watchOrgPayrollPayments(
  orgId: string,
  onData: (payments: PayrollPayment[]) => void,
  onError?: (err: unknown) => void,
): () => void {
  const q = query(collection(getFirestoreInstance(), 'organizations', orgId, 'payrollPayments'), orderBy('paidAt', 'desc'));
  return onSnapshot(q, snap => onData(snap.docs.map(d => fromFirestore<PayrollPayment>(d.data()))), err => onError?.(err));
}
